import type { TaskPriority } from '@/lib/api/types';
import { useT } from '@/lib/i18n';
import { PRIORITY_LABELS } from '@/lib/priority';
import { cn } from '@/lib/utils';

/**
 * Priority as a small coloured dot.
 *
 * Colours come from theme tokens (`--color-priority-*`), never from literals in
 * a component — spec §13. NONE renders nothing at all: an absent signal should
 * take no visual weight.
 */
const STYLES: Record<TaskPriority, string | null> = {
  URGENT: 'bg-priority-urgent',
  HIGH: 'bg-priority-high',
  NORMAL: 'bg-priority-normal',
  LOW: 'bg-priority-low',
  NONE: null,
};

export function PriorityDot({
  priority,
  className,
}: {
  priority: TaskPriority;
  className?: string;
}) {
  const t = useT();
  const dot = STYLES[priority];
  if (!dot) {
    // A placeholder of the same size keeps titles aligned across rows.
    return <span className={cn('h-1.5 w-1.5 shrink-0', className)} aria-hidden />;
  }

  const label = t(PRIORITY_LABELS[priority]);
  return (
    <span
      className={cn('h-1.5 w-1.5 shrink-0 rounded-full', dot, className)}
      title={label}
      aria-label={`${t('priority.label')}: ${label}`}
    />
  );
}
